import React, { useState } from 'react';
import { Card } from './ui/Card';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote } from 'lucide-react';
import { RadiantStar } from './ui/RadiantStar';
type Review = {
  shortText: string;
  longText: string;
  author: string;
  role?: string;
};
const reviews: Review[] = [{
  shortText: 'Mark, your coaching style—structured yet flexible, challenging yet safe—made growth feel achievable and inspired real change in me. With your encouragement and insightful questions, I found the confidence to step out of my comfort zone, build sustainable habits, and shift from supporting to proactively shaping outcomes.',
  longText: 'Mark, your coaching style has been both structured and flexible, creating a safe space where growth felt not only possible but truly achievable. You balanced encouragement with practical challenge, helping me turn big intentions into small, effective habits—like mailbox routines, time-blocking, and setting clear boundaries. Through our role plays and your insightful questions, I gained the confidence to communicate more openly and handle complex team dynamics with greater clarity.\n\nYour unwavering support and positive energy inspired me to step out of my comfort zone and discover new strengths. This coaching journey was genuinely transformative: I shifted from a support role to proactively shaping outcomes, building resilience, and finding renewed energy in my work. I deeply appreciate the trust you built and the honest dialogue we shared. Thank you for guiding me on this path of continuous growth—I look forward to carrying these lessons forward.',
  author: 'Marjut',
  role: '(Finnish)'
}];
export function ReviewsSection() {
  return <section className="relative py-24 overflow-hidden bg-earth-900/30" data-id="element-106">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-400/20 to-transparent" data-id="element-107"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-400/20 to-transparent" data-id="element-108"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-109">
        <div className="text-center mb-16" data-id="element-110">
          <div className="flex justify-center mb-4" data-id="element-111">
            <RadiantStar size={48} animate={true} data-id="element-112" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-cream-50 mb-4" data-id="element-113">
            What Others Have Found
          </h2>
          <p className="text-gold-400 text-lg font-medium tracking-wide uppercase" data-id="element-114">
            Stories of Growth
          </p>
        </div>

        <div className="grid md:grid-cols-1 gap-8 max-w-4xl mx-auto" data-id="element-115">
          {reviews.map((review, index) => <ReviewCard key={index} review={review} data-id="element-116" />)}
        </div>
      </div>
    </section>;
}
function ReviewCard({
  review
}: {
  review: Review;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return <div className="relative h-full" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-117">
      <Card className="h-full relative overflow-hidden group cursor-default transition-all duration-300 border-gold-400/20 hover:border-gold-400/40" data-id="element-118">
        {/* Default View (Short Text) */}
        <div className="flex flex-col h-full p-2" data-id="element-119">
          <Quote className="text-gold-400 mb-4 opacity-50" size={32} data-id="element-120" />

          <p className="text-cream-50/90 text-lg leading-relaxed italic mb-6 flex-grow" data-id="element-121">
            "{review.shortText}"
          </p>

          <div className="mt-auto" data-id="element-122">
            <p className="text-gold-400 font-serif text-lg" data-id="element-123">
              — {review.author}{' '}
              <span className="text-gold-400/70 text-base" data-id="element-124">{review.role}</span>
            </p>

            <div className="mt-4 flex items-center justify-center" data-id="element-125">
              <span className="text-xs text-gold-400/60 uppercase tracking-widest border-b border-gold-400/20 pb-0.5 group-hover:text-gold-400 transition-colors" data-id="element-126">
                Hover for full review
              </span>
            </div>
          </div>
        </div>

        {/* Hover Overlay (Long Text) */}
        <AnimatePresence data-id="element-127">
          {isHovered && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="absolute inset-0 bg-night-900/95 backdrop-blur-sm p-8 flex flex-col justify-center overflow-y-auto scrollbar-hide z-20" data-id="element-128">
              <div className="prose prose-invert max-w-none" data-id="element-129">
                {review.longText.split('\n\n').map((paragraph, i) => <p key={i} className="text-cream-50 text-base leading-relaxed mb-4 last:mb-0" data-id="element-130">
                    {paragraph}
                  </p>)}
              </div>

              <div className="mt-6 pt-4 border-t border-gold-400/20" data-id="element-131">
                <p className="text-gold-400 font-serif text-lg" data-id="element-132">
                  — {review.author}{' '}
                  <span className="text-gold-400/70 text-base" data-id="element-133">
                    {review.role}
                  </span>
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Card>
    </div>;
}